import cx from "classnames";
import isEqual from "lodash/isEqual";
import React from "react";
import { Checkbox, Photo, TextArea } from "component-library";
import { withTranslation, WithTranslation } from "i18n";
import { IPhoto } from "models/api";
import { IMakeSuggestionsPerformer, MembershipTypes } from "models/suggestion";
import { getImageUrl, ImageLocation } from "services/urlBuilder";
import { PhotoGalleryWrapper } from "components/MakeSuggestionsView/PhotoGalleryWrapper/PhotoGalleryWrapper";
import { IMakeSuggestionsCardTexts } from "components/SuggestionsViewAgent/SuggestionsViewAgent.helpers";
import {
  findPhoto,
  getExpirationDate,
  getFormattedYoungPerformerAge,
  getMembershipType,
  getPhotos,
} from "./Card.helpers";
import "./Card.scss";
import TagminAvailabilityCalendar from "./TagminAvailabilityCalendar";

const PERFORMER_NOTE_MAX_LENGTH = 1000;

export interface ICardComposeProps {
  performer: IMakeSuggestionsPerformer;
  texts: IMakeSuggestionsCardTexts;
  onSelectChange: (item: IMakeSuggestionsPerformer, value: boolean) => void;
  onCommentChange: (item: IMakeSuggestionsPerformer, value: string) => void;
  onPhotoSelect: (item: IMakeSuggestionsPerformer, value: string) => void;
  wrapperWidth: number;
}

export interface ICardProps extends ICardComposeProps, WithTranslation {}

export interface ICardState {
  isSelected: boolean;
  comment: string;
  selectedPhoto: IPhoto;
}

export class Card extends React.Component<ICardProps, ICardState> {
  private cardContainerRef = React.createRef<HTMLDivElement>();

  public readonly state: Readonly<ICardState> = {
    isSelected: !!this.props.performer.suggestion,
    comment: this.props.performer.suggestion?.comment ?? "",
    selectedPhoto: findPhoto(this.props.performer),
  };

  shouldComponentUpdate(
    nextProps: Readonly<ICardProps>,
    nextState: Readonly<ICardState>
  ): boolean {
    const { performer, texts, wrapperWidth } = this.props;

    return (
      !isEqual(this.state, nextState) ||
      !isEqual(nextProps.performer, performer) ||
      !isEqual(nextProps.texts, texts) ||
      wrapperWidth !== nextProps.wrapperWidth
    );
  }

  public render() {
    const { performer, texts, t } = this.props;
    const { isSelected, selectedPhoto } = this.state;
    const fullName = `${performer.forenames} ${performer.surname}`;
    const hasTagminCalendar =
      !isSelected && !!performer.tagminAvailability?.length;

    return (
      <div
        ref={this.cardContainerRef}
        className={cx("c-performer-grid-card", {
          "c-performer-grid-card--selected g-bg-secondary": isSelected,
          "c-performer-grid-card--tagmin": hasTagminCalendar,
        })}
        id={performer.artistRef}
      >
        <div className="c-performer-grid-card__base-content">
          <div className="c-performer-grid-card__main-info">
            <div className="c-performer-grid-card__image-container">
              <div className="c-performer-grid-card__image">
                <Photo
                  src={getImageUrl(
                    selectedPhoto.fileName,
                    ImageLocation.MainPhoto
                  )}
                  alt={fullName}
                  hasLoadingSpinner={true}
                />
              </div>
              {performer.expiryDate && (
                <div className="c-performer-grid-card__expiration-warning">
                  {getExpirationDate(
                    texts.membershipExpires,
                    performer.expiryDate
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="c-performer-grid-card__content">
            <div>
              <a
                href={performer._links.profile.href}
                target="_blank"
                className="c-performer-grid-card__name g-alternative-link g-alternative-link--md"
                tabIndex={0}
                rel="noreferrer"
              >
                {fullName.toUpperCase()}
              </a>
            </div>
            <span className="c-performer-grid-card__artist-type">
              {getMembershipType(
                performer.performerType,
                texts.membershipTypes
              )}
            </span>
            {performer.performerType === MembershipTypes.youngPerformer &&
              performer.dateOfBirth && (
                <span className="c-performer-grid-card__young-performer-age">
                  {getFormattedYoungPerformerAge(t, performer.dateOfBirth)}
                </span>
              )}
            <div className="c-performer-grid-card__media-icons">
              {performer.hasVideoClip && (
                <span
                  className="icon-showreel"
                  title={texts.videoShowreelTooltip}
                />
              )}
              {performer.hasAudioClip && (
                <span className="icon-audio" title={texts.voiceClipsTooltip} />
              )}
            </div>
          </div>
        </div>
        {isSelected && this.renderCustomInfoSection()}
        {hasTagminCalendar && (
          <TagminAvailabilityCalendar
            tagminAvailability={performer.tagminAvailability}
          />
        )}
        <Checkbox
          htmlId="performerGridSelect"
          className="c-performer-grid-card__select"
          name="selectCard"
          ariaLabel="selectCard"
          value="selectCard"
          onChange={this.handleCheckboxChange}
          checked={isSelected}
        />
        <div className="c-performer-grid-card__cv-icons">
          {performer.hasNudge && (
            <span
              className="icon-nudge c-performer-grid-card__cv-icon"
              title={texts.clientNudged}
            />
          )}
          {performer.hasSelfSuggestion && (
            <span
              className="c-performer-grid-card__cv-icon"
              title={texts.clientSubmittedThemselves}
            >
              !
            </span>
          )}
        </div>
      </div>
    );
  }

  private renderCustomInfoSection() {
    const { performer, texts, wrapperWidth } = this.props;
    const { comment, selectedPhoto } = this.state;

    return (
      <div className="c-performer-grid-card__custom-details">
        <PhotoGalleryWrapper
          photos={getPhotos(performer)}
          selectedPhotoId={selectedPhoto.id}
          moreButtonText={texts.morePhotos}
          onSelect={this.handlePhotoSelect}
          wrapperWidth={wrapperWidth}
        />
        <div className="c-performer-grid-card__textarea-wrapper">
          <TextArea
            id="customInfoText"
            isResizable
            autoResizeMobile
            placeholder={texts.addComment}
            warningLength={PERFORMER_NOTE_MAX_LENGTH - 1}
            maxLength={PERFORMER_NOTE_MAX_LENGTH}
            value={comment}
            valueChanged={this.handleCommentValueChanged}
            onBlur={this.saveCommentValueChanged}
            showsCharacterCount
            height={{ lg: 80, md: 80, sm: 100 }}
          />
        </div>
      </div>
    );
  }

  readonly handleCheckboxChange = () => {
    this.setState({ isSelected: !this.state.isSelected }, () =>
      this.props.onSelectChange(this.props.performer, this.state.isSelected)
    );
  };

  private handlePhotoSelect = (selectedPhoto: IPhoto) => {
    this.setState({ selectedPhoto }, () =>
      this.props.onPhotoSelect(this.props.performer, selectedPhoto.id)
    );
  };

  private handleCommentValueChanged = (comment: string) => {
    this.setState({ comment });
  };

  readonly saveCommentValueChanged = () => {
    const { performer, onCommentChange } = this.props;
    const { comment } = this.state;

    if (comment !== performer.suggestion?.comment) {
      onCommentChange(performer, comment);
    }
  };
}

export default withTranslation()(Card) as React.ComponentType<
  React.PropsWithChildren<ICardComposeProps>
>;
